/******************************************************************************
 * Copyright (c) 2026 Contributors to the Eclipse Foundation.
 *
 * See the NOTICE file(s) distributed with this work for additional
 * information regarding copyright ownership.
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * https://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *****************************************************************************/

import { DateTime } from 'luxon';
import { addQuery, createAbsoluteURL, sendRequest, UrlString } from 'openvsx-webui';
import {
  RegistryAnalyticsAgents,
  RegistryAnalyticsExtensions,
  RegistryAnalyticsInterval,
  RegistryAnalyticsNamespaces,
  RegistryAnalyticsOverview,
  RegistryAnalyticsSeries,
  RegistryAnalyticsStatus,
  RegistryAnalyticsVersionSeries,
  RegistryAnalyticsVersions
} from './analytics-types';
import { RangeWindow, fixedWindow, formatBound, resolveBound } from './date-expressions';

// Bindings for the registry analytics API, which the Eclipse Foundation deployment adds on top
// of the open-source registry — so they live here rather than in `ExtensionRegistryService`.
// Every endpoint requires analytics access; `from`/`to` are UTC dates (yyyy-MM-dd) or ISO instants,
// `from` inclusive, `to` exclusive. Callers may pass clock-relative bounds ("now-6h", see
// date-expressions): they are resolved here, as each request is sent, so a refetch of a live
// window moves with time.

type Query = { key: string; value: string | number | undefined }[];

/** The query with its `from`/`to` resolved against the clock at this fetch. */
function atFetchTime(queries: Query): Query {
  const now = DateTime.utc();
  return queries.map((query) =>
    (query.key === 'from' || query.key === 'to') && typeof query.value === 'string'
      ? { ...query, value: formatBound(resolveBound(query.value, now)) }
      : query
  );
}

/** One query pair per value of a repeated parameter ('agent', 'namespace', 'extension'). */
const listQueries = (key: string, values?: string[]): Query => (values ?? []).map((value) => ({ key, value }));

function analyticsRequest<T>(
  serverUrl: string,
  signal: AbortSignal,
  segment: string,
  queries: Query
): Promise<Readonly<T>> {
  return sendRequest({
    signal,
    credentials: true,
    endpoint: createAbsoluteURL([serverUrl, 'api', '-', 'analytics', 'registry', segment], atFetchTime(queries))
  });
}

/**
 * Whether the caller has analytics access at all: the endpoint answering is the capability.
 * A 403 (no access) or 404 (module not deployed) both mean the section must stay hidden.
 */
export function getRegistryAnalyticsStatus(
  serverUrl: string,
  signal: AbortSignal
): Promise<Readonly<RegistryAnalyticsStatus>> {
  return analyticsRequest(serverUrl, signal, 'status', []);
}

export function getRegistryAnalyticsExtensions(
  serverUrl: string,
  signal: AbortSignal,
  params: {
    query?: string;
    namespaces?: string[];
    category?: string;
    agents?: string[];
    from?: string;
    to?: string;
    interval?: RegistryAnalyticsInterval;
    sort?: 'downloads' | 'growth' | 'name';
    direction?: 'asc' | 'desc';
    offset?: number;
    size?: number;
  }
): Promise<Readonly<RegistryAnalyticsExtensions>> {
  return analyticsRequest(serverUrl, signal, 'extensions', [
    { key: 'query', value: params.query },
    ...listQueries('namespace', params.namespaces),
    { key: 'category', value: params.category },
    ...listQueries('agent', params.agents),
    { key: 'from', value: params.from },
    { key: 'to', value: params.to },
    { key: 'interval', value: params.interval },
    { key: 'sort', value: params.sort },
    { key: 'direction', value: params.direction },
    { key: 'offset', value: params.offset },
    { key: 'size', value: params.size }
  ]);
}

/** A series response with the bounds its request resolved to: a live window, pinned to this fetch. */
export type FetchedSeries = RegistryAnalyticsSeries & { window: RangeWindow };

export async function getRegistryAnalyticsSeries(
  serverUrl: string,
  signal: AbortSignal,
  params: {
    /** 'namespace.name' references, optionally pinned to one version with an '@version' suffix. */
    extensions: string[];
    agents?: string[];
    from: string;
    to: string;
    interval?: RegistryAnalyticsInterval;
  }
): Promise<Readonly<FetchedSeries>> {
  // pinned to this fetch's clock and returned with the data, so an export can name what was
  // actually fetched and a growth baseline can start exactly where it ends
  const window = fixedWindow(params);
  const response = await analyticsRequest<RegistryAnalyticsSeries>(serverUrl, signal, 'series', [
    ...listQueries('extension', params.extensions),
    ...listQueries('agent', params.agents),
    { key: 'from', value: window.from },
    { key: 'to', value: window.to },
    { key: 'interval', value: params.interval }
  ]);
  return { ...response, window };
}

export function getRegistryAnalyticsVersionSeries(
  serverUrl: string,
  signal: AbortSignal,
  params: {
    extension: string;
    agents?: string[];
    from?: string;
    to?: string;
    interval?: RegistryAnalyticsInterval;
    size?: number;
    /** Version to always keep apart, so the caller can highlight it. */
    version?: string;
  }
): Promise<Readonly<RegistryAnalyticsVersionSeries>> {
  return analyticsRequest(serverUrl, signal, 'version-series', [
    { key: 'extension', value: params.extension },
    ...listQueries('agent', params.agents),
    { key: 'from', value: params.from },
    { key: 'to', value: params.to },
    { key: 'interval', value: params.interval },
    { key: 'size', value: params.size },
    { key: 'version', value: params.version }
  ]);
}

export function getRegistryAnalyticsVersions(
  serverUrl: string,
  signal: AbortSignal,
  params: { extension: string; agents?: string[]; from?: string; to?: string }
): Promise<Readonly<RegistryAnalyticsVersions>> {
  return analyticsRequest(serverUrl, signal, 'versions', [
    { key: 'extension', value: params.extension },
    ...listQueries('agent', params.agents),
    { key: 'from', value: params.from },
    { key: 'to', value: params.to }
  ]);
}

export function getRegistryAnalyticsOverview(
  serverUrl: string,
  signal: AbortSignal,
  params: {
    namespaces?: string[];
    category?: string;
    agents?: string[];
    from?: string;
    to?: string;
    interval?: RegistryAnalyticsInterval;
  }
): Promise<Readonly<RegistryAnalyticsOverview>> {
  return analyticsRequest(serverUrl, signal, 'overview', [
    ...listQueries('namespace', params.namespaces),
    { key: 'category', value: params.category },
    ...listQueries('agent', params.agents),
    { key: 'from', value: params.from },
    { key: 'to', value: params.to },
    { key: 'interval', value: params.interval }
  ]);
}

export function getRegistryAnalyticsNamespaces(
  serverUrl: string,
  signal: AbortSignal,
  params: { query?: string; category?: string; agents?: string[]; from?: string; to?: string; size?: number }
): Promise<Readonly<RegistryAnalyticsNamespaces>> {
  return analyticsRequest(serverUrl, signal, 'namespaces', [
    { key: 'query', value: params.query },
    { key: 'category', value: params.category },
    ...listQueries('agent', params.agents),
    { key: 'from', value: params.from },
    { key: 'to', value: params.to },
    { key: 'size', value: params.size }
  ]);
}

/** The endpoint is the capability URL from `RegistryAnalyticsStatus.agentsUrl`, not a known path. */
export function getRegistryAnalyticsAgents(
  signal: AbortSignal,
  agentsUrl: UrlString,
  params: { from?: string; to?: string; size?: number }
): Promise<Readonly<RegistryAnalyticsAgents>> {
  return sendRequest({
    signal,
    credentials: true,
    endpoint: addQuery(
      agentsUrl,
      atFetchTime([
        { key: 'from', value: params.from },
        { key: 'to', value: params.to },
        { key: 'size', value: params.size }
      ])
    )
  });
}
